import React from 'react';
import { RepairTableStyles as styles } from './RepairTableStyles';
import { Trash2, X } from 'lucide-react'; // Importing delete and close icons

interface RepairListingsProps {
  repairs: any[];
  onRepairAction: (repair: any, action: string) => void;
  onSparePartsView: (repair: any) => void;
  onDeleteRepair?: (repair: any) => void; // Optional delete handler
}

export const RepairListings: React.FC<RepairListingsProps> = ({ 
  repairs, 
  onRepairAction,
  onSparePartsView,
  onDeleteRepair
}) => {
  const calculateDaysDifference = (startDate: string, endDate: string) => {
    if (!startDate || !endDate) return 0;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diff = Math.abs(end.getTime() - start.getTime());
    return Math.floor(diff / (1000 * 60 * 60 * 24)); // Convert milliseconds to days
  };

  // Color palette for different status buttons
  const statusColors = {
    1: "#6c757d",    // Unassigned - Gray
    2: "#28a745",    // In Progress - Green
    3: "#17a2b8",    // Pending Approval - Teal
    4: "#007bff",    // Booking - Blue
    5: "#ffc107",    // Receiving - Yellow
    6: "#fd7e14",    // Releasing - Orange
    7: "#6f42c1",    // Closing - Purple
    8: "#20c997"     // Edit - Mint Green
  };

  const renderActionButton = (repair: any, action: string, label: string) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <button
        style={{
          ...styles.actionButton,
          backgroundColor: statusColors[repair.StatusID] || "#007bff",
          color: 'white',
          border: 'none',
          padding: '6px 12px',
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          gap: '5px',
          cursor: 'pointer', // Add this line to change the cursor
          transition: 'background-color 0.3s ease' // Optional: add a smooth hove
        }}
        onClick={() => onRepairAction(repair, action)}
      >
        {label}
      </button>
      {onDeleteRepair && (
        <button
          style={{
            backgroundColor: 'transparent',
            border: '1px solid #dc3545',
            color: '#dc3545',
            padding: '6px',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          onClick={() => onDeleteRepair(repair)}
          title="Delete Repair"
        >
          <Trash2 size={16} />
        </button>
      )}
    </div>
  );

  return (
    <table style={styles.table}>
      <thead>
        <tr>
          <th style={styles.th}>Reg No</th>
          <th style={styles.th}>Request Date</th>
          <th style={styles.th}>Date Booked</th>
          <th style={styles.th}>Date Released</th>
          <th style={styles.th}>Days at Workshop</th>
          <th style={styles.th}>Issue</th>
          <th style={styles.th}>Assigned To</th>
          <th style={styles.th}>Mech Notes</th>
          <th style={styles.th}>Spare Cost</th>
          <th style={styles.th}>Labour Cost</th>
          <th style={styles.th}>Status</th>
          <th style={styles.th}>Actions</th>
        </tr>
      </thead>
      <tbody>
        {repairs.map((repair) => (
          <tr key={repair.RepairID} style={styles.tr}>
            <td style={styles.td}>{repair.VregNO}</td>
            <td style={styles.td}>
              {repair.DateAdded
                ? new Date(repair.DateAdded).toLocaleDateString()
                : "-"}
            </td>
            <td style={styles.td}>
              {repair.ReceivedDate
                ? new Date(repair.ReceivedDate).toLocaleDateString()
                : "-"}
            </td>
            <td style={styles.td}>
              {repair.ReleaseDate
                ? new Date(repair.ReleaseDate).toLocaleDateString()
                : "-"}
            </td>
            <td style={styles.td}>
              {calculateDaysDifference(
                repair?.ReceivedDate,
                repair?.ReleaseDate
              )}
            </td>
            <td style={styles.td}>{repair.RepairDescription}</td>
            <td style={styles.td}>{repair.AssignedToName || "Unassigned"}</td>
            <td style={styles.td}>{repair.MechNotes}</td>
            <td
              style={{ ...styles.td, cursor: "pointer" }}
              onClick={() => onSparePartsView(repair)}
            >
              {repair.SpareCost !== 0
                ? `KES ${repair.SpareCost.toLocaleString()}`
                : "-"}
            </td>
            <td style={styles.td}>
              {repair.RepairCost !== 0
                ? `KES ${repair.RepairCost.toLocaleString()}`
                : "-"}
            </td>
            <td style={styles.td}>{repair.StatusName}</td>
            <td style={styles.td}>
              {(repair.StatusID === 1 || repair.StatusID === 2 || 
                repair.StatusID === 3 || repair.StatusID === 4 || 
                repair.StatusID === 5 || repair.StatusID === 6 || 
                repair.StatusID === 7 || repair.StatusID === 8) && 
                renderActionButton(
                  repair, 
                  repair.StatusID === 1 ? "assign" :
                  repair.StatusID === 2 ? "review" :
                  repair.StatusID === 3 ? "approve" :
                  repair.StatusID === 4 ? "book" :
                  repair.StatusID === 5 ? "receive" :
                  repair.StatusID === 6 ? "release" :
                  repair.StatusID === 7 ? "close" : "edit",
                  repair.StatusID === 1 ? "Assign" :
                  repair.StatusID === 2 ? "Review" :
                  repair.StatusID === 3 ? "Approve" :
                  repair.StatusID === 4 ? "Book" :
                  repair.StatusID === 5 ? "Receive" :
                  repair.StatusID === 6 ? "Release" :
                  repair.StatusID === 7 ? "Close" : "Edit"
                )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};